/**
 * Selection keys for the header viewer.
 * One parser for tree, editor, and preview — do not re-implement prefixes in app code.
 */

export interface ParsedWidgetTreeKey {
  name: string;
  section: string | null;
  elementName: string | null;
  screenId?: string | null;
  indices: number[];
}

export function parseWidgetTreeKey(key: string | null | undefined): ParsedWidgetTreeKey | null {
  if (!key?.startsWith("widget:")) return null;
  const rest = key.slice("widget:".length);
  const slash = rest.indexOf("/");
  if (slash === -1) {
    return { name: rest, section: null, elementName: null, indices: [] };
  }
  const name = rest.slice(0, slash);
  const path = rest.slice(slash + 1);
  if (path === "visual" || path.startsWith("visual.")) {
    const suffix = path.length > "visual".length ? path.slice("visual.".length) : "";
    const indices = suffix ? suffix.split(".").map(Number) : [];
    return { name, section: "visual", elementName: null, indices };
  }
  if (path === "elements") {
    return { name, section: "elements", elementName: null, indices: [] };
  }
  if (path === "panels") {
    return { name, section: "panels", elementName: null, indices: [] };
  }
  if (path.startsWith("elements/")) {
    const tail = path.slice("elements/".length);
    const dot = tail.indexOf(".");
    const elementName = dot === -1 ? tail : tail.slice(0, dot);
    const indices = dot === -1 ? [] : tail.slice(dot + 1).split(".").map(Number);
    return { name, section: "elements", elementName, indices };
  }
  if (path === "group" || path.startsWith("group/")) {
    if (path === "group") {
      return { name, section: "group", screenId: null, elementName: null, indices: [] };
    }
    const tail = path.slice("group/".length);
    const dot = tail.indexOf(".");
    const screenId = dot === -1 ? tail : tail.slice(0, dot);
    const indices = dot === -1 ? [] : tail.slice(dot + 1).split(".").map(Number);
    return { name, section: "group", screenId, elementName: null, indices };
  }
  return { name: rest, section: null, elementName: null, indices: [] };
}

export function parseWidgetKey(key: string | null | undefined): string | null {
  return parseWidgetTreeKey(key)?.name ?? null;
}

export function widgetStorageKey(name: string): string {
  return `widget:${name}`;
}

export function widgetVisualTreeKey(name: string, indices: number[] = []): string {
  if (!indices.length) return `widget:${name}/visual`;
  return `widget:${name}/visual.${indices.join(".")}`;
}

export function widgetElementTreeKey(name: string, elementName: string, indices: number[] = []): string {
  if (!indices.length) return `widget:${name}/elements/${elementName}`;
  return `widget:${name}/elements/${elementName}.${indices.join(".")}`;
}

export function widgetElementsFolderKey(name: string): string {
  return `widget:${name}/elements`;
}

export function widgetGroupFolderKey(name: string): string {
  return `widget:${name}/group`;
}

export function widgetGroupTreeKey(name: string, screenId: string, indices: number[] = []): string {
  if (!indices.length) return `widget:${name}/group/${screenId}`;
  return `widget:${name}/group/${screenId}.${indices.join(".")}`;
}

export function isWidgetGroupTreeKey(key: string | null | undefined): boolean {
  return parseWidgetTreeKey(key)?.section === "group";
}

export function parseChromeKey(key: string | null | undefined): {
  part: string;
  pageKey: string | null;
  frameRef: string | null;
} | null {
  if (!key?.startsWith("chrome:")) return null;
  const rest = key.slice("chrome:".length);
  if (rest.startsWith("pages/")) {
    return { part: "pages", pageKey: rest.slice("pages/".length), frameRef: null };
  }
  if (rest === "frame") return { part: "frame", pageKey: null, frameRef: null };
  if (rest.startsWith("frame/")) {
    const frameRef = rest.slice("frame/".length);
    return { part: "frame", pageKey: null, frameRef: frameRef || null };
  }
  return { part: rest, pageKey: null, frameRef: null };
}

export function chromeFrameWidgetKey(ref: string): string {
  return `chrome:frame/${ref}`;
}

export function chromeStorageKey(part: string): string {
  return `chrome:${part}`;
}

export function chromePageStorageKey(pageKey: string): string {
  return `chrome:pages/${pageKey}`;
}

export function isChromeTreeKey(key: string | null | undefined): boolean {
  return Boolean(parseChromeKey(key));
}

export function chromePartSectionKey(part: string): string {
  return `section:chrome:${part}`;
}

export function chromePageSectionKey(pageKey: string): string {
  return `section:chrome:pages:${pageKey}`;
}

export function isWidgetVisualTreeKey(key: string | null | undefined): boolean {
  return parseWidgetTreeKey(key)?.section === "visual";
}

export function isWidgetElementTreeKey(key: string | null | undefined): boolean {
  const parsed = parseWidgetTreeKey(key);
  return Boolean(parsed?.section === "elements" && parsed.elementName);
}

export interface ParsedElementKey {
  scope: string;
  name: string;
  indices: number[];
}

function splitElementNameAndIndices(path: string): { name: string; indices: number[] } | null {
  const dot = path.indexOf(".");
  if (dot === -1) return { name: path, indices: [] };
  const name = path.slice(0, dot);
  if (!name) return null;
  const indices: number[] = [];
  for (const segment of path.slice(dot + 1).split(".")) {
    if (!segment) continue;
    if (!/^\d+$/.test(segment)) return null;
    indices.push(Number(segment));
  }
  return { name, indices };
}

export function parseElementKey(key: string | null | undefined): ParsedElementKey | null {
  if (!key?.startsWith("element:")) return null;
  const rest = key.slice("element:".length);
  const colon = rest.indexOf(":");
  const scope = colon === -1 ? "global" : rest.slice(0, colon);
  if (!scope) return null;
  const split = splitElementNameAndIndices(colon === -1 ? rest : rest.slice(colon + 1));
  if (!split?.name) return null;
  return { scope, name: split.name, indices: split.indices };
}

export function elementStorageKey(scope: string, name: string): string {
  return scope && scope !== "global" ? `element:${scope}:${name}` : `element:${name}`;
}

export function elementTreeKey(scope: string, name: string, indices: number[] = []): string {
  const base = elementStorageKey(scope, name);
  if (!indices.length) return base;
  return `${base}.${indices.join(".")}`;
}

export function parseStateKey(key: string | null | undefined): { surface: string | null; indices: number[] } | null {
  if (!key) return null;
  const prefixed = key.match(/^([a-z0-9_-]+):state(?:\.(.+))?$/);
  if (prefixed) {
    return {
      surface: prefixed[1],
      indices: prefixed[2] ? prefixed[2].split(".").map(Number) : [],
    };
  }
  if (key === "state") return { surface: null, indices: [] };
  if (key.startsWith("state.")) {
    return {
      surface: null,
      indices: key.slice("state.".length).split(".").map(Number),
    };
  }
  return null;
}

export function isStateNodeKey(key: string | null | undefined): boolean {
  if (!key || key.startsWith("element:") || key.startsWith("widget:")) return false;
  return key === "state" || key.startsWith("state.") || /^[a-z0-9_-]+:state(\.|$)/.test(key);
}

export function stateStorageKey(surface: string | null | undefined): string {
  return surface ? `${surface}:state` : "state";
}
